import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { useLanguage } from "@/contexts";
import { NewPromptActionLabel } from "@/hooks/useNewPromptData";
import { useModels } from "@/hooks/useModels";

// Lightweight inline tooltip and icon helpers (no external deps)
const InfoIcon: FC<{ className?: string; }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const Tooltip: FC<{ text: string; children: ReactNode; }> = ({ text, children }) => (
  <span className="relative inline-flex items-center group cursor-default">
    {children}
    <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-72 sm:w-80 md:w-96 p-3 bg-gray-800 dark:bg-slate-900 text-white dark:text-slate-100 text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50 whitespace-normal break-words">
      {text}
      <svg className="absolute text-gray-800 dark:text-slate-900 h-2 w-4 left-1/2 -translate-x-1/2 top-full" viewBox="0 0 255 255">
        <polygon className="fill-current" points="0,0 127.5,127.5 255,0" />
      </svg>
    </span>
  </span>
);

export interface NewPromptFormProps {
  // Field values
  title: string;
  description: string;
  fullText: string;
  systemprompt: string;
  isPublic: boolean;
  selectedModel: string;

  // UI/derived state
  isSystemPromptExpanded: boolean;
  isEnhanced: boolean;
  hasBeenEnhanced: boolean;

  // Async state
  loading: boolean;
  isEnhancing: boolean;

  // Validation/errors
  errors: { title?: string; description?: string; full_text?: string; };
  apiError: string | null;
  error: string | null;

  // Contextual primary action button
  actionButtonLabel: NewPromptActionLabel;
  actionButtonDisabled: boolean;
  actionButtonTooltip?: string;

  // Setters
  setTitle: React.Dispatch<React.SetStateAction<string>>;
  setDescription: React.Dispatch<React.SetStateAction<string>>;
  setFullText: React.Dispatch<React.SetStateAction<string>>;
  setSystemprompt: React.Dispatch<React.SetStateAction<string>>;
  setIsPublic: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSystemPromptExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedModel: React.Dispatch<React.SetStateAction<string>>;

  // Actions
  onActionClick: () => void;
  onRevertClick: () => void;
  onClearClick: () => void;

  // Submit
  onSubmit: (data: {
    title: string;
    description: string;
    full_text: string;
    systemprompt: string;
    is_public: boolean;
    model_type: string;
  }) => Promise<void>;
}

const NewPromptForm: FC<NewPromptFormProps> = ({
  // Field values
  title,
  description,
  fullText,
  systemprompt,
  isPublic,
  selectedModel,

  // UI/derived state
  isSystemPromptExpanded,
  isEnhanced,

  // Async state
  loading,
  isEnhancing,

  // Validation/errors
  errors,
  apiError,
  error,

  // Contextual primary action button
  actionButtonLabel,
  actionButtonDisabled,
  actionButtonTooltip,

  // Setters
  setTitle,
  setDescription,
  setFullText,
  setSystemprompt,
  setIsPublic,
  setIsSystemPromptExpanded,
  setSelectedModel,

  // Actions
  onActionClick,
  onRevertClick,
  onClearClick,

  // Submit
  onSubmit,
}) => {
  const { t } = useLanguage();
  const { models, loading: modelsLoading } = useModels();
  // Auto-scroll management for streaming enhancement
  const promptTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const prevIsEnhancingRef = useRef(false);
  const [userScrolledAway, setUserScrolledAway] = useState(false);

  // Reset scroll-away flag at enhancement start
  useEffect(() => {
    if (isEnhancing && !prevIsEnhancingRef.current) {
      setUserScrolledAway(false);
    }
    prevIsEnhancingRef.current = isEnhancing;
  }, [isEnhancing]);

  // Auto-scroll to bottom as new chunks append during enhancement
  useEffect(() => {
    if (!isEnhancing) return;
    if (userScrolledAway) return;
    const el = promptTextareaRef.current;
    if (!el) return;
    // Scroll to bottom smoothly
    el.scrollTop = el.scrollHeight;
  }, [fullText, isEnhancing, userScrolledAway]);

  // Get selected model details
  const selectedModelData = models.find(model => model.name === selectedModel);

  // Custom submit handler to map camelCase to snake_case for backend
  const onFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({
      title,
      description,
      full_text: fullText,
      systemprompt,
      is_public: isPublic,
      model_type: selectedModel,
    });
  };

  return (
    <div className="w-full relative">
      <h2 className="text-2xl font-bold mb-8 text-center text-brand-dark-blue dark:text-slate-100 transition-colors duration-300">
        {t("createNewPrompt")}
      </h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 -mx-6 border-t border-gray-200 dark:border-slate-700 px-6 py-6 transition-colors duration-300">
        {/* Left Column - Main Form Fields */}
        <div className="lg:col-span-2 space-y-6">
          <form id="new-prompt-form" onSubmit={onFormSubmit}>
            {/* Title Field */}
            <div className="mb-6">
              <label htmlFor="title" className="block text-sm font-semibold mb-2 text-left text-gray-700 dark:text-slate-200 transition-colors duration-200">{t("title")}</label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg text-left bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 transition-colors duration-200 ${errors.title ? "border-red-400 dark:border-red-500" : "border-gray-300 dark:border-slate-600"} focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:border-brand-electric-blue`}
                placeholder={t("titlePlaceholder")}
                required
                data-testid="prompt-title-input"
                disabled={loading || isEnhancing}
              />
              {errors.title && <div className="text-red-500 dark:text-red-400 text-xs mt-1 text-left transition-colors duration-200">{errors.title}</div>}
            </div>

            {/* Short Description Field */}
            <div className="mb-6">
              <label htmlFor="description" className="block text-sm font-semibold mb-2 text-left text-gray-700 dark:text-slate-200 transition-colors duration-200">{t("shortDescription")}</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg text-left bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 transition-colors duration-200 ${errors.description ? "border-red-400 dark:border-red-500" : "border-gray-300 dark:border-slate-600"} focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:border-brand-electric-blue`}
                rows={3}
                placeholder={t("shortDescriptionPlaceholder")}
                required
                data-testid="prompt-description-input"
                disabled={loading || isEnhancing}
              />
              {errors.description && <div className="text-red-500 dark:text-red-400 text-xs mt-1 text-left transition-colors duration-200">{errors.description}</div>}
            </div>

            {/* Generate Initial Prompt Button */}
            <div className="my-0 flex justify-center flex-col items-center">
              {isEnhanced ? (
                <button
                  type="button"
                  onClick={onRevertClick}
                  className="inline-flex items-center px-4 py-2 text-sm font-bold text-white bg-red-600 dark:bg-red-700 border border-red-600 dark:border-red-700 rounded-md hover:bg-red-700 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 shadow-md transition-colors duration-200"
                >
                  <svg className="mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                  </svg>
                  {t("revertToOriginal")}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onActionClick}
                  disabled={actionButtonDisabled || loading || isEnhancing}
                  className={`flex items-center gap-2 px-4 py-2 border font-semibold rounded-md transition duration-200 ${
                    actionButtonDisabled || loading || isEnhancing
                      ? 'border-gray-300 dark:border-slate-600 text-gray-400 dark:text-slate-500 bg-gray-100 dark:bg-slate-800 cursor-not-allowed'
                      : 'border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30'
                  }`}
                  title={actionButtonTooltip}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.868 2.884c.321-.772 1.415-.772 1.736 0l1.681 4.048 4.448.648c.81.118 1.135 1.106.55 1.648l-3.22 3.138.76 4.43c.133.78-.71.138-1.423-.388l-3.975-2.09-3.975 2.09c-.713.526-1.556 1.168-1.423-.388l.76-4.43-3.22-3.138c-.585-.542-.26-1.53.55-1.648l4.448-.648 1.681-4.048z" clipRule="evenodd" />
                  </svg>
                  {actionButtonLabel}
                </button>
              )}
            </div>

            {/* System Prompt Collapsible Section */}
            <div className="mb-10 mt-10">
              <button
                type="button"
                onClick={() => setIsSystemPromptExpanded(!isSystemPromptExpanded)}
                className="flex items-center justify-between w-full p-2 text-sm font-medium text-gray-700 dark:text-slate-200 bg-gray-50 dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-md transition duration-200 border border-gray-200 dark:border-slate-600"
                aria-expanded={isSystemPromptExpanded}
              >
                <span>{t("systemPromptOptionalAdvanced")}</span>
                <svg 
                  className={`h-4 w-4 transition-transform ${isSystemPromptExpanded ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  isSystemPromptExpanded ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="relative">
                  <textarea
                    id="system_prompt"
                    value={systemprompt}
                    onChange={(e) => setSystemprompt(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg text-left bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:border-brand-electric-blue"
                    rows={3}
                    placeholder={t("systemPromptPlaceholder")}
                    data-testid="prompt-systemprompt-input"
                    disabled={loading || isEnhancing}
                  />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-sm text-gray-500 dark:text-slate-400 transition-colors duration-200">
                      {systemprompt.length} {t('characters')}
                    </p>
                    <span />
                  </div>
                </div>
              </div>
            </div>

            {/* Prompt Field */}
            <div className="mb-8">
                            <div className="flex items-center justify-between mb-2">
                <label htmlFor="full_text" className="block text-sm font-semibold text-left text-gray-700 dark:text-slate-200 transition-colors duration-200">{t("prompt")}</label>
              </div>
              <div className="relative">
                <textarea
                  id="full_text"
                  ref={promptTextareaRef}
                  value={fullText}
                  onChange={(e) => setFullText(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg text-left bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 transition-colors duration-200 ${errors.full_text ? "border-red-400 dark:border-red-500" : "border-gray-300 dark:border-slate-600"} ${isEnhanced ? "border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20" : ""} focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:border-brand-electric-blue`}
                  style={{ whiteSpace: 'pre-wrap' }}
                  rows={6}
                  placeholder={t("fullTextPlaceholder")}
                  required
                  data-testid="prompt-fulltext-input"
                  disabled={loading || isEnhancing}
                  onScroll={(e) => {
                    const el = e.currentTarget;
                    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
                    // If user scrolls more than ~140px away from bottom, pause auto-scroll
                    setUserScrolledAway(distanceFromBottom > 140);
                  }}
                />
                {isEnhancing && (
                  <div className="absolute inset-0 bg-gray-50 dark:bg-slate-700/75 bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center rounded-lg transition-colors duration-200">
                    <div className="flex items-center text-gray-600 dark:text-slate-300">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('enhancingPrompt')}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between mt-2">
                <p className="text-sm text-gray-500 dark:text-slate-400 transition-colors duration-200">
                  {fullText.length} {t('characters')}
                </p>
                {isEnhanced && (
                  <span className="text-xs text-green-600 dark:text-green-400 font-medium transition-colors duration-200">{t('enhancedVersion')}</span>
                )}
              </div>
              {errors.full_text && <div className="text-red-500 dark:text-red-400 text-xs mt-1 text-left transition-colors duration-200">{errors.full_text}</div>}
            </div>

            {/* Error Messages */}
            {apiError && <div className="text-red-600 dark:text-red-400 text-sm mb-4 text-center transition-colors duration-200">{apiError}</div>}
            {error && (
              <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg relative my-4 transition-colors duration-200" role="alert">
                <strong className="font-bold">Error: </strong>
                <span className="block sm:inline">{error}</span>
              </div>
            )}

            {/* Bottom spacer */}
            <div className="pt-6" />
          </form>
        </div>

        {/* Right Column - Model Configuration and Visibility */}
        <div className="space-y-6">
          {/* Model Configuration */}
          <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 border border-gray-200 dark:border-slate-700 sticky top-8 text-left transition-colors duration-300">
            <h3 className="text-base font-semibold text-gray-800 dark:text-slate-100 mb-4 inline-flex items-center justify-start text-left transition-colors duration-200">{t('modelConfiguration')}</h3>
            
            {/* Model Dropdown */}
            <label htmlFor="model-select" className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1 transition-colors duration-200">{t("model")}</label>
            <select
              id="model-select"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full mb-4 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm font-medium bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:border-brand-electric-blue"
              disabled={modelsLoading}
            >
              {modelsLoading ? (
                <option>{t('loadingModels')}</option>
              ) : (
                models.map((model) => (
                  <option key={model.id} value={model.name}>
                    {model.display_name}
                  </option>
                ))
              )}
            </select>

            {/* Model Details */}
            {selectedModelData && (
              <div className="space-y-3 text-sm text-gray-600 dark:text-slate-300 transition-colors duration-200">
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1.5">
                    <span className="font-medium">{t('contextWindow')}</span>
                    <Tooltip text="The maximum number of tokens (words/characters) the model can consider from the input.">
                      <InfoIcon className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                    </Tooltip>
                  </span>
                  <span className="font-medium text-gray-800 dark:text-slate-100">{selectedModelData.context_window.toLocaleString()} tokens</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1.5">
                    <span className="font-medium">{t('maxOutput')}</span>
                    <Tooltip text="The maximum number of tokens the model can generate in a single response.">
                      <InfoIcon className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                    </Tooltip>
                  </span>
                  <span className="font-medium text-gray-800 dark:text-slate-100">4K tokens</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1.5">
                    <span className="font-medium">{t('pricePer1MTokens')}</span>
                    <Tooltip text="Pricing is based on the number of tokens in your input (prompt) and the model's output (response).">
                      <InfoIcon className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                    </Tooltip>
                  </span>
                  <span className="font-medium text-gray-800 dark:text-slate-100">
                    ${selectedModelData.input_cost_per_million_tokens?.toFixed(2) || '0.00'} / ${selectedModelData.output_cost_per_million_tokens?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <p className="text-xs text-center text-gray-500 dark:text-slate-400 pt-2 transition-colors duration-200">{t('inputOutput')}</p>
              </div>
            )}
          </div>

          {/* Visibility Toggle */}
          <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-slate-700 text-left transition-colors duration-300">
            <h3 className="text-base font-semibold text-gray-800 dark:text-slate-100 mb-3 inline-flex items-center justify-start text-left transition-colors duration-200">{t('visibility')}</h3>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700 dark:text-slate-200 transition-colors duration-200">{isPublic ? t("public") : t("private")}</span>
              <button
                type="button"
                onClick={() => setIsPublic(!isPublic)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue ${
                  isPublic ? 'bg-brand-dark-blue' : 'bg-gray-300 dark:bg-slate-600'
                }`}
                role="switch"
                aria-checked={isPublic}
                aria-label={`Toggle prompt visibility to ${isPublic ? t("private") : t("public")}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    isPublic ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-xs text-gray-600 dark:text-slate-400 transition-colors duration-200">
              {isPublic ? t('promptVisibility.anyoneWithLink') : t('promptVisibility.onlyYou')}
            </p>
          </div>

          {/* Right column bottom spacing */}
          <div className="pt-2" />
        </div>
      </div>

      {/* Full-width footer actions */}
      <div className="-mx-6 -mb-6 bg-gray-50 dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 px-6 py-3 flex items-center justify-between transition-colors duration-300">
        <button
          type="button"
          onClick={onClearClick}
          className="text-sm font-medium text-gray-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 transition-colors duration-200"
          disabled={loading || isEnhancing}
        >
          {t("clearFields")}
        </button>
        <button
          type="submit"
          form="new-prompt-form"
          className="px-6 py-2.5 bg-brand-dark-blue dark:bg-blue-600 text-white font-semibold rounded-md shadow-sm hover:bg-brand-electric-blue dark:hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading || isEnhancing}
        >
          {loading ? t("submitting") : t("createPrompt")}
        </button>
      </div>
    </div>
  );
};

export default NewPromptForm;
